import { useQueryClient } from '@tanstack/react-query';
import { assert } from '@varos/util-typescript';
export const SNIPPET_LIST_QUERY_KEY = ['research', 'snippet', 'find'];
export function getSnippetLookupKey(snippetId) {
    return ['research', 'snippet', 'lookup', { snippetId }];
}
export function createSnippetRepository(config) {
    const { repository, client } = config;
    return {
        useFind(context, props, options) {
            const queryClient = useQueryClient();
            const query = repository.useQuery({
                ...options,
                queryKey: [...SNIPPET_LIST_QUERY_KEY, props],
                async queryFn() {
                    const { data: snippets } = await client.user.snippet.list(props);
                    return snippets;
                },
                onSuccess(data) {
                    for (const snippet of data) {
                        const queryKey = getSnippetLookupKey(snippet.id);
                        queryClient.setQueryData(queryKey, snippet);
                    }
                },
            });
            return query;
        },
        useLookup(context, props, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getSnippetLookupKey(props.snippetId),
                async queryFn() {
                    const snippet = await client.user.snippet.retrieve(props.snippetId);
                    return snippet;
                },
            });
            return query;
        },
        useLookupMaybe(context, id, options) {
            const query = repository.useQuery({
                ...options,
                queryKey: getSnippetLookupKey(id),
                async queryFn() {
                    const { data: [snippet = null, ...rest], } = await client.user.snippet.list({ id });
                    assert(rest.length === 0, 'expected only one snippet');
                    return snippet;
                },
            });
            return query;
        },
        useCreate(context) {
            const queryClient = useQueryClient();
            const mutation = repository.useMutation(context, {
                async mutationFn(payload) {
                    const response = await client.user.snippet.create(payload);
                    return response;
                },
                async onSuccess(data) {
                    const queryKey = getSnippetLookupKey(data.id);
                    queryClient.setQueryData(queryKey, data);
                    queryClient.refetchQueries({
                        queryKey: [...SNIPPET_LIST_QUERY_KEY],
                    });
                },
            });
            return mutation;
        },
        useUpdate(context) {
            const queryClient = useQueryClient();
            const mutation = repository.useMutation(context, {
                async mutationFn({ snippetId, payload, }) {
                    const response = await client.user.snippet.update(snippetId, payload);
                    return response;
                },
                async onSuccess(data, { snippetId }) {
                    // Update the snippet in the lookup cache
                    const lookupQueryKey = getSnippetLookupKey(snippetId);
                    queryClient.setQueryData(lookupQueryKey, data);
                    // Refetch related queries
                    await queryClient.refetchQueries({
                        queryKey: [...SNIPPET_LIST_QUERY_KEY],
                    });
                },
            });
            return mutation;
        },
        useDelete(context, options = {}) {
            const queryClient = useQueryClient();
            const mutation = repository.useMutation(context, {
                ...options,
                async mutationFn(props) {
                    const data = await client.user.snippet.delete(props.snippetId);
                    await queryClient.refetchQueries({
                        queryKey: [...SNIPPET_LIST_QUERY_KEY],
                    });
                    return data;
                },
            });
            return mutation;
        },
    };
}
//# sourceMappingURL=snippetRepository.js.map